import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Printer, Check, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { MediabilityLogo } from "@/components/Logo";
import { ProposalDocument, NAV } from "@/components/proposal/ProposalDocument";
import { cn } from "@/lib/utils";

export default function PublicQuotePage() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState(null); // accept | changes | question
  const [name, setName] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [active, setActive] = useState("summary");

  useEffect(() => {
    document.title = "Proposal - Mediability";
    api
      .get(`/public/quotes/${token}`)
      .then((r) => {
        setData(r.data);
        document.title = `${r.data.quote?.title || "Proposal"} - Mediability`;
        if (params.get("print") === "1") setTimeout(() => window.print(), 900);
      })
      .catch((e) => setError(errMsg(e)));
  }, [token, params]);

  // section spy for the top navigation
  useEffect(() => {
    if (!data) return undefined;
    const els = NAV.map((n) => document.getElementById(n.id)).filter(Boolean);
    if (!els.length || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        const vis = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (vis[0]) setActive(vis[0].target.id);
      },
      { rootMargin: "-30% 0px -55% 0px", threshold: 0 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [data]);

  const respond = async () => {
    setBusy(true);
    try {
      await api.post(`/public/quotes/${token}/respond`, { action: mode, message: msg, name });
      setDone(mode);
      setMode(null);
      toast.success(mode === "accept" ? "Thank you - the proposal is accepted" : "Thank you - your message has been sent");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6" data-testid="public-quote-error">
        <div className="max-w-md rounded-[20px] border border-black/[0.07] bg-white p-10 text-center shadow-float">
          <MediabilityLogo className="mx-auto h-7" />
          <h1 className="font-display mt-8 text-[22px] font-semibold tracking-[-0.02em]">This proposal link is not valid</h1>
          <p className="mt-3 text-[14px] leading-6 text-muted-foreground">{error}. Please contact your Mediability representative for an updated link.</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-sm text-muted-foreground" data-testid="public-quote-loading">
        <span className="mr-2 h-2 w-2 rounded-full bg-primary pulse-dot" /> Loading proposal
      </div>
    );
  }

  const q = data.quote;
  const accepted = q.customer_accepted_at || done === "accept";
  const owner = data.owner || {};

  return (
    <div className="min-h-screen bg-background text-foreground print:bg-white" data-testid="public-quote-page">
      <div className="no-print sticky top-0 z-30 bg-[hsl(var(--background)/0.86)] backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-[1120px] items-center justify-between gap-4 px-4 sm:px-8">
          <MediabilityLogo className="h-6 sm:h-7" />
          <nav className="hidden items-center gap-1 rounded-full border border-black/[0.06] bg-white/80 p-1 md:flex" aria-label="Proposal sections" data-testid="public-quote-nav">
            {NAV.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => scrollTo(n.id)}
                data-testid={`public-quote-nav-${n.id}`}
                className={cn(
                  "h-8 rounded-full px-3.5 text-[12.5px] font-medium text-muted-foreground transition-colors duration-150 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40",
                  active === n.id && "bg-[hsl(var(--ink))] text-white hover:text-white"
                )}
              >
                {n.label}
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="hidden gap-1.5 rounded-full sm:inline-flex" onClick={() => window.print()} data-testid="public-quote-download-pdf-button">
              <Printer className="h-3.5 w-3.5" /> PDF
            </Button>
            {!accepted && (
              <Button size="sm" className="gap-1.5 rounded-full" onClick={() => { setMode("accept"); scrollTo("acceptance"); }} data-testid="public-quote-accept-top-button">
                <Check className="h-3.5 w-3.5" /> Accept proposal
              </Button>
            )}
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-[1120px] px-4 pb-16 pt-4 sm:px-8 sm:pt-8 print:max-w-none print:p-0">
        <ProposalDocument data={data} response={{ mode, setMode, name, setName, msg, setMsg, busy, respond, done }} />
        <p className="no-print mt-8 text-center text-[12px] text-muted-foreground">
          Shared by {owner.name} at Mediability. <a href="https://mediability.com" target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 hover:text-foreground">mediability.com <ArrowUpRight className="h-3 w-3" /></a>
        </p>
      </main>
    </div>
  );
}
